import { Details } from '@details/Interfaces/details';
import { DetailsSection } from '@details/Interfaces/details-section';
import { LayoutOrientation } from '@common/enums/layout-orientation';
import { DetailsItemTypesArray , blank, nameOnly, simple, anchor, DetailsItemType, DetailsItem } from '@details/Interfaces/details-item-types';

type CheckValue = boolean | string;

export function has(...args: any[]): boolean {
  const r = args.filter(a => a != undefined && a.toString().trim() !== '');
  return r.length > 0;
}

export function prop<T, K extends keyof T>(obj: T, key: K) {
  return obj[key];
}

export class DetailsBuilder<T> {

  private readonly details: Details;
  private currentSection: DetailsSection;

  constructor(header: string, private readonly model: T) {

    this.details = {
      header: header,
      sections: [] as DetailsSection[]
    }

  }

  conditionalSection(checkValue: CheckValue, header: string, orientation: LayoutOrientation, subHeader: string = undefined) {
    if (checkValue) {
      const section = {
        header: header,
        subHeader: subHeader,
        items: [],
        orientation: orientation
      };
      this.details.sections.push(section);
      this.currentSection = section;
    } else {
      this.currentSection = null;
    }

    return this;
  }

  section(header: string, orientation: LayoutOrientation) {
    this.conditionalSection(true, header, orientation);
    return this;
  }

  conditionalItem(checkValue: CheckValue, item: DetailsItemType) {
    if (checkValue) {
      if (this.currentSection) {
        this.currentSection.items.push(item);
      }
    }
    return this;
  }

  always(item: DetailsItemType) {
    this.conditionalItem(true, item);
    return this;
  }

  itemFor(a: keyof T, item: DetailsItemType) {
    this.conditionalItem(has(prop(this.model, a)), item);
    return this;
  }

  for(a: keyof T, name: string, formatter?: (s: string)=> string ) {
    const v = `${prop(this.model, a)}`;
    const formatted = formatter ? formatter(v) : v;
    this.itemFor(a, simple(name, formatted ));
    return this;
  }

  anchorFor(a: keyof T, name: string, scheme: string) {
    const v = `${prop(this.model, a)}`;
    this.itemFor(a, anchor(name, `${scheme}:${v}`, v));
    return this;
  }

  telFor(a: keyof T, name: string) {
    this.anchorFor(a, name, 'tel');
    return this;
  }

  mailFor(a: keyof T, name: string) {
    this.anchorFor(a, name, 'mailto');
    return this;
  }

  linkForConditionalFormatting(checkValue: CheckValue, a: keyof T, name: string) {
    const v = `${prop(this.model, a)}`;
    if (checkValue) {
      this.itemFor(a, anchor(name, v, v, true));
    } else {
      this.itemFor(a, simple(name, v));
    }
    return this;
  }

  getDetails(): Details {
    return this.details;
  }

}


